import React, { useState, memo, useRef } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import Turnstile from 'react-turnstile';

// Move FormContent outside the main component to prevent recreation
const FormContent = memo(({ 
  formData, 
  onChange, 
  turnstileToken, 
  setTurnstileToken,
  submitting, 
  submitError, 
  submitSuccess,
  TURNSTILE_SITE_KEY,
  formRef,
  onFormSubmit 
}) => (
  <form ref={formRef} className="space-y-4">
    {/* FormSubmit required fields */}
    <input type="hidden" name="_subject" value="New contact form submission from GxT International website" />
    <input type="hidden" name="_captcha" value="true" />
    <input type="hidden" name="_template" value="box" />
    <input type="hidden" name="_next" value={typeof window !== 'undefined' ? window.location.href : ''} />
    
    {/* Form fields */}
    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
        Full Name
      </label>
      <input
        type="text"
        id="name"
        name="name"
        value={formData.name}
        onChange={onChange}
        required
        disabled={submitting}
        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-[#fff8f3] placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        placeholder="Enter your full name"
      />
    </div>

    <div>
      <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
        Email Address
      </label>
      <input
        type="email"
        id="email"
        name="email"
        value={formData.email}
        onChange={onChange}
        required
        disabled={submitting}
        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-[#fff8f3] placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        placeholder="Enter your email address"
      />
    </div>

    <div>
      <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
        Message
      </label>
      <textarea
        id="message"
        name="message"
        value={formData.message}
        onChange={onChange}
        required
        rows="4"
        disabled={submitting}
        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-[#fff8f3] placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        placeholder="Your message here..."
      />
    </div>

    {/* Cloudflare Turnstile */}
    <div className="mb-4 flex justify-center">
      <Turnstile
        sitekey={TURNSTILE_SITE_KEY}
        onVerify={(token) => setTurnstileToken(token)}
        theme="dark"
        className="transform scale-90"
      />
    </div>

    {/* Error message */}
    {submitError && (
      <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
        {submitError}
      </div>
    )}

    {/* Success message */}
    {submitSuccess && (
      <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 text-sm">
        ✓ Thank you for your message! We'll contact you soon.
      </div>
    )}

    <button
      type="button"
      onClick={onFormSubmit}
      disabled={submitting || !turnstileToken}
      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-[#fff8f3] font-semibold py-3 px-6 rounded-[30px] transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
    >
      {submitting ? (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Sending...
        </span>
      ) : (
        'Send Message'
      )}
    </button>
  </form>
));

const Contact = () => {
  const [activeCard, setActiveCard] = useState('info'); // 'form' or 'info'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Form reference for direct submission
  const formRef = useRef(null);

  // FormSubmit automatically sends to support@gxtigroup.com via the form action
  
  // Your Cloudflare Turnstile site key
  const TURNSTILE_SITE_KEY = '0x4AAAAAACf_JGD8iRun9FS2';

  const handleFormSubmit = () => {
    if (!turnstileToken) {
      setSubmitError('Please complete the security verification');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    // Create a hidden input for Turnstile token
    const turnstileInput = document.createElement('input');
    turnstileInput.type = 'hidden';
    turnstileInput.name = 'cf-turnstile-response';
    turnstileInput.value = turnstileToken;
    formRef.current.appendChild(turnstileInput);

    // Set the form action dynamically
    formRef.current.action = 'https://formsubmit.co/support@gxtigroup.com';
    formRef.current.method = 'POST';
    
    // Submit the form
    formRef.current.submit();
    
    // Show success message
    setSubmitSuccess(true);
    setFormData({ name: '', email: '', message: '' });
    setTurnstileToken(null);
    setSubmitting(false);

    // Remove the Turnstile input after submission
    setTimeout(() => {
      if (turnstileInput.parentNode) {
        turnstileInput.remove();
      }
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-12 md:py-20 bg-gradient-to-b from-gray-800 to-gray-900 min-h-screen flex items-center">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center mb-4 bg-gray-800 rounded-full px-4 py-2 border border-gray-700">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2 animate-pulse"></div>
            <span className="text-blue-400 font-medium">Get In Touch</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#fff8f3] mb-4">Contact Us</h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
            Reach out for expert IT consulting and training solutions
          </p>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-red-500 to-red-400 mx-auto mt-4 md:mt-6 rounded-full"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Mobile Layout - Stacked Vertically */}
          <div className="block md:hidden space-y-6">
            {/* Contact Information Card - Always on top */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700 p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mr-3">
                  <span className="text-blue-400 font-bold">01</span>
                </div>
                <h3 className="text-2xl font-bold text-[#fff8f3]">Contact Information</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start group hover:bg-gray-700/50 p-3 rounded-lg transition-all duration-300">
                  <EnvelopeIcon className="h-6 w-6 text-blue-400 mr-4 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-300">Email</p>
                    <a 
                      href="mailto:support@gxtigroup.com" 
                      className="text-[#fff8f3] hover:text-blue-400 transition"
                    >
                      support@gxtigroup.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-xl font-bold text-[#fff8f3] mb-4">Why Choose Us</h4>
                <ul className="space-y-3">
                  <li className="flex items-center group">
                    <div className="h-2 w-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-300">Expert IT Security Consulting</span>
                  </li>
                  <li className="flex items-center group">
                    <div className="h-2 w-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-300">Industry-Leading Training Programs</span>
                  </li>
                  <li className="flex items-center group">
                    <div className="h-2 w-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-300">Compliance & Certification Support</span>
                  </li>
                  <li className="flex items-center group">
                    <div className="h-2 w-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-300">Customized Business Solutions</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Form Card - Below */}
            <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700 p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mr-3">
                  <span className="text-blue-400 font-bold">02</span>
                </div>
                <h3 className="text-2xl font-bold text-[#fff8f3]">Send a Message</h3>
              </div>
              <FormContent 
                formData={formData}
                onChange={handleChange}
                turnstileToken={turnstileToken}
                setTurnstileToken={setTurnstileToken}
                submitting={submitting}
                submitError={submitError}
                submitSuccess={submitSuccess}
                TURNSTILE_SITE_KEY={TURNSTILE_SITE_KEY}
                formRef={formRef}
                onFormSubmit={handleFormSubmit}
              />
            </div>
          </div>

          {/* Desktop Layout - Interactive Cards */}
          <div className="hidden md:block relative" style={{ minHeight: '600px' }}>
            {/* Contact Form Card */}
            <div
              onClick={() => setActiveCard('form')}
              className={`absolute left-0 right-0 transition-all duration-700 ease-in-out cursor-pointer
                ${activeCard === 'form' 
                  ? 'transform scale-100 z-20 opacity-100' 
                  : 'transform scale-90 z-10 opacity-90 hover:opacity-100'
                }`}
              style={{
                top: activeCard === 'form' ? '0' : '20px',
                width: activeCard === 'form' ? '70%' : '60%',
                marginLeft: activeCard === 'form' ? '0' : '5%',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className={`bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border ${
                activeCard === 'form' ? 'border-blue-500 shadow-blue-500/20' : 'border-gray-700'
              }`}>
                <div className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mr-3">
                      <span className="text-blue-400 font-bold">01</span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#fff8f3]">Send a Message</h3>
                  </div>
                  <FormContent 
                    formData={formData}
                    onChange={handleChange}
                    turnstileToken={turnstileToken}
                    setTurnstileToken={setTurnstileToken}
                    submitting={submitting}
                    submitError={submitError}
                    submitSuccess={submitSuccess}
                    TURNSTILE_SITE_KEY={TURNSTILE_SITE_KEY}
                    formRef={formRef}
                    onFormSubmit={handleFormSubmit}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Card */}
            <div
              onClick={() => setActiveCard('info')}
              className={`absolute left-0 right-0 transition-all duration-700 ease-in-out cursor-pointer
                ${activeCard === 'info' 
                  ? 'transform scale-100 z-20 opacity-100' 
                  : 'transform scale-90 z-10 opacity-90 hover:opacity-100'
                }`}
              style={{
                top: activeCard === 'info' ? '0' : '20px',
                width: activeCard === 'info' ? '70%' : '60%',
                marginLeft: activeCard === 'info' ? '30%' : '35%',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden border ${
                activeCard === 'info' ? 'border-blue-500 shadow-blue-500/20' : 'border-gray-700'
              }`}>
                <div className="p-8 md:p-12">
                  <div className="flex items-center mb-8">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mr-3">
                      <span className="text-blue-400 font-bold">02</span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#fff8f3]">Contact Information</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="flex items-start group hover:bg-gray-700/50 p-3 rounded-lg transition-all duration-300">
                      <EnvelopeIcon className="h-6 w-6 text-blue-400 mr-4 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-300">Email</p>
                        <a 
                          href="mailto:support@gxtigroup.com" 
                          className="text-[#fff8f3] hover:text-blue-400 transition"
                        >
                          support@gxtigroup.com
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12">
                    <h4 className="text-xl font-bold text-[#fff8f3] mb-6">Why Choose Us</h4>
                    <ul className="space-y-4">
                      <li className="flex items-center group">
                        <div className="h-2 w-2 bg-blue-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-300"></div>
                        <span className="text-gray-300 group-hover:text-[#fff8f3] transition-colors duration-300">
                          Expert IT Security Consulting
                        </span>
                      </li>
                      <li className="flex items-center group">
                        <div className="h-2 w-2 bg-blue-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-300"></div>
                        <span className="text-gray-300 group-hover:text-[#fff8f3] transition-colors duration-300">
                          Industry-Leading Training Programs
                        </span>
                      </li>
                      <li className="flex items-center group">
                        <div className="h-2 w-2 bg-blue-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-300"></div>
                        <span className="text-gray-300 group-hover:text-[#fff8f3] transition-colors duration-300">
                          Compliance & Certification Support
                        </span>
                      </li>
                      <li className="flex items-center group">
                        <div className="h-2 w-2 bg-blue-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-300"></div>
                        <span className="text-gray-300 group-hover:text-[#fff8f3] transition-colors duration-300">
                          Customized Business Solutions
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-700">
                    <p className="text-sm text-gray-500">
                      Click to {activeCard === 'info' ? 'minimize' : 'expand'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Click instruction - Desktop only */}
        <div className="hidden md:block text-center mt-20">
          <p className="text-gray-500 text-sm animate-pulse">
            Click on either card to swap their sizes
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;